import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton, useTheme } from '@mui/material';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { useLogin } from '../../contexts/LoginContext';
import PremiumCard from '../ui/PremiumCard';
import StatusBadge from '../ui/StatusBadge';
import { buscarPlanoAtual } from './planoClienteApi';
import { IPlanoContratado } from './types';

interface PlanoClienteCardProps {
  variant?: 'dashboard' | 'drawer';
}

export const PlanoClienteCard: React.FC<PlanoClienteCardProps> = ({ variant = 'dashboard' }) => {
  const [login] = useLogin();
  const theme = useTheme();
  const [plano, setPlano] = useState<IPlanoContratado | null>(null);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<boolean>(false);
  const [statusCode, setStatusCode] = useState<number | null>(null);

  const clienteID = login.usuario?.usuarioCliente?.clienteID;

  useEffect(() => {
    if (!clienteID || !login.accessToken) {
      setCarregando(false);
      return;
    }

    setCarregando(true);
    setErro(false);

    buscarPlanoAtual(clienteID, login.accessToken)
      .then((res) => {
        setPlano(res.data);
        setStatusCode(res.status);
        setCarregando(false);
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          setStatusCode(404);
          setErro(false);
          setPlano(null);
        } else {
          console.error('Erro ao buscar plano', err);
          setErro(true);
          if (err.response) {
            setStatusCode(err.response.status);
          }
        }
        setCarregando(false);
      });
  }, [clienteID, login.accessToken]);

  if (carregando) {
    if (variant === 'drawer') {
      return (
        <Box sx={{ margin: '8px 16px' }}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="80%" height={16} />
        </Box>
      );
    }
    return (
      <PremiumCard sx={{ height: '100%', minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Skeleton variant="text" width="40%" height={24} />
        <Skeleton variant="rectangular" height={60} sx={{ my: 2, borderRadius: 1 }} />
        <Skeleton variant="text" width="80%" height={20} />
      </PremiumCard>
    );
  }

  // Se der erro ou não houver plano cadastrado
  if (erro || !plano || plano.status === 'nao_informado') {
    const mensagemErro = (statusCode === 404 || plano?.status === 'nao_informado')
      ? 'Plano não informado. Entre em contato com o suporte para consultar as informações do seu plano.'
      : 'Não foi possível carregar as informações do plano.';

    if (variant === 'drawer') {
      return (
        <Box sx={{ margin: '8px 16px', p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
            Plano não informado
          </Typography>
        </Box>
      );
    }

    return (
      <PremiumCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 180 }}>
        <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CardMembershipIcon color="disabled" /> Meu Plano
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {mensagemErro}
        </Typography>
      </PremiumCard>
    );
  }

  const isAtivo = plano.status === 'ativo';

  const getBadgeStatus = (statusStr: string) => {
    const statusLower = statusStr.toLowerCase();
    if (statusLower === 'ativo') return 'saudavel';
    if (statusLower === 'suspenso' || statusLower === 'cancelado') return 'critico';
    return 'atencao'; // vencido
  };

  if (variant === 'drawer') {
    return (
      <Box sx={{ margin: '8px 16px', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(0, 0, 0, 0.03)' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
          Seu Plano ({plano.descricaoTipoPlano})
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.2 }}>
          {plano.valorFormatado}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>
            /{plano.tipoPlano === 'mensal' ? 'mês' : plano.tipoPlano === 'anual' ? 'ano' : plano.tipoPlano}
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.72rem' }}>
          {plano.validadeFormatada}
        </Typography>
      </Box>
    );
  }

  return (
    <PremiumCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CardMembershipIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            Meu Plano
          </Typography>
          <StatusBadge status={getBadgeStatus(plano.status)} label={plano.statusFormatado} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.02em', display: 'flex', alignItems: 'baseline' }}>
          {plano.valorFormatado}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 500 }}>
            {plano.periodicidadeFormatada}
          </Typography>
        </Typography>

        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, mb: 0.5 }}>
          {plano.descricaoTipoPlano}
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block">
          {plano.validadeFormatada}
        </Typography>
      </Box>

      {isAtivo && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Renovação em {plano.diasRestantes} {plano.diasRestantes === 1 ? 'dia' : 'dias'}
          </Typography>
        </Box>
      )}
    </PremiumCard>
  );
};

export default PlanoClienteCard;
